import { OffsetUnit, WorkflowActionId } from "@sp-crm/core";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { Select } from "components/ui/select";
import { Spinner } from "components/ui/spinner";
import { produce } from "immer";
import React from "react";
import {
    computeWorkflowDelay,
    hasEditableDelay,
    isTokenDelayRecipe,
} from "./manage-workflows-helper";
import { EditableWorkflow } from "./types";

interface DelayRecipeEditorProps {
    workflow: EditableWorkflow;
    onCommit: (delayRecipe: string) => Promise<void>;
}

export const WAIT_SAVE_FAILURE_MESSAGE = "The wait could not be saved. Try again.";

export interface DelayRecipeSource {
    key: string;
    delay: number;
    delayRecipe: string | null;
    editable: boolean;
    waitable: boolean;
}

export const workflowDelaySource = (workflow: EditableWorkflow): DelayRecipeSource => ({
    key: workflow.id,
    delay: workflow.delay,
    delayRecipe: workflow.delayRecipe ?? null,
    editable: !!workflow.regionVisibility?.editable,
    waitable: hasEditableDelay(workflow),
});

export const actionIntervalSource = (
    workflow: EditableWorkflow,
    actionId: WorkflowActionId,
): DelayRecipeSource => {
    const action = workflow.actions.find(candidate => candidate.id === actionId);
    return {
        key: `${workflow.id}:${actionId}`,
        delay: 0,
        delayRecipe: action?.delayRecipe ?? null,
        editable: !!workflow.regionVisibility?.editable,
        waitable: true,
    };
};

interface State {
    status: "pristine" | "dirty" | "pending" | "failed" | "ignore";
    sourceKey: string;
    upstreamDelay: number;
    upstreamUnits: OffsetUnit;
    currentDelay: number;
    currentUnit: OffsetUnit;
}

const initialState = (source: DelayRecipeSource): State => {
    if (isTokenDelayRecipe(source.delayRecipe)) {
        return {
            sourceKey: source.key,
            status: "ignore",
            upstreamDelay: source.delay,
            upstreamUnits: "d",
            currentDelay: source.delay,
            currentUnit: "d",
        };
    }

    const [delay, units] = computeWorkflowDelay(source);

    return {
        sourceKey: source.key,
        status: "pristine",
        upstreamDelay: delay,
        upstreamUnits: units,
        currentDelay: delay,
        currentUnit: units,
    };
};

type Action =
    | { type: "changeDelay"; delay: number }
    | { type: "changeUnit"; unit: OffsetUnit }
    | { type: "upstreamChanged"; source: DelayRecipeSource }
    | { type: "commit"; delay: number; unit: OffsetUnit }
    | { type: "commitFailed" };

const reducer = (state: State, action: Action): State => {
    return produce(state, draft => {
        if (action.type === "changeDelay") {
            draft.status = "dirty";
            draft.currentDelay = action.delay;
        }

        if (action.type === "changeUnit") {
            draft.status = "dirty";
            draft.currentUnit = action.unit;
        }

        if (action.type === "upstreamChanged") {
            const anchoredToDateField = isTokenDelayRecipe(action.source.delayRecipe);

            if (
                action.source.key !== draft.sourceKey ||
                anchoredToDateField !== (state.status === "ignore")
            ) {
                Object.assign(draft, initialState(action.source));
            } else {
                const [delay, units] = computeWorkflowDelay(action.source);

                draft.upstreamDelay = delay;
                draft.upstreamUnits = units;

                if (
                    state.status === "pending" &&
                    state.currentDelay === delay &&
                    state.currentUnit === units
                ) {
                    draft.status = "pristine";
                }

                if (state.status === "pristine") {
                    draft.currentDelay = delay;
                    draft.currentUnit = units;
                }
            }
        }

        if (action.type === "commit") {
            draft.status =
                state.upstreamDelay === action.delay &&
                state.upstreamUnits === action.unit
                    ? "pristine"
                    : "pending";
            draft.currentDelay = action.delay;
            draft.currentUnit = action.unit;
        }

        if (action.type === "commitFailed") {
            draft.status = "failed";
            draft.currentDelay = state.upstreamDelay;
            draft.currentUnit = state.upstreamUnits;
        }
    });
};

const wholeDelay = (delay: number): number => (isNaN(delay) ? 0 : Math.floor(delay));

export interface DelayRecipeEditorControl {
    visible: boolean;
    editable: boolean;
    pending: boolean;
    failed: boolean;
    delay: number;
    unit: OffsetUnit;
    handleDelayChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleUnitsChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    commitDelayChange: () => void;
    commitDelay: (delay: number, unit: OffsetUnit) => void;
}

export const useDelaySourceEditor = (
    source: DelayRecipeSource,
    onCommit: (delayRecipe: string) => Promise<void>,
): DelayRecipeEditorControl => {
    const [state, dispatch] = React.useReducer(reducer, source, initialState);

    const commitDelay = React.useCallback(
        (delay: number, unit: OffsetUnit) => {
            dispatch({ type: "commit", delay, unit });
            onCommit(`${delay}${unit}`).catch(() => dispatch({ type: "commitFailed" }));
        },
        [onCommit],
    );

    const handleDelayChange = React.useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            dispatch({ type: "changeDelay", delay: e.target.valueAsNumber });
        },
        [],
    );

    const handleUnitsChange = React.useCallback(
        (e: React.ChangeEvent<HTMLSelectElement>) => {
            const newUnit = e.target.value as OffsetUnit;
            dispatch({ type: "changeUnit", unit: newUnit });
            commitDelay(wholeDelay(state.currentDelay), newUnit);
        },
        [state.currentDelay, commitDelay],
    );

    const commitDelayChange = React.useCallback(() => {
        if (state.status !== "dirty") {
            return;
        }

        commitDelay(wholeDelay(state.currentDelay), state.currentUnit);
    }, [state.status, state.currentDelay, state.currentUnit, commitDelay]);

    React.useEffect(() => {
        dispatch({ type: "upstreamChanged", source });
    }, [source.key, source.delay, source.delayRecipe]); // eslint-disable-line react-hooks/exhaustive-deps

    return {
        visible: state.status !== "ignore" && source.waitable,
        editable: source.editable,
        pending: state.status === "pending",
        failed: state.status === "failed",
        delay: state.currentDelay,
        unit: state.currentUnit,
        handleDelayChange,
        handleUnitsChange,
        commitDelayChange,
        commitDelay,
    };
};

export const useDelayRecipeEditor = (
    workflow: EditableWorkflow,
    onCommit: (delayRecipe: string) => Promise<void>,
): DelayRecipeEditorControl =>
    useDelaySourceEditor(workflowDelaySource(workflow), onCommit);

export const DelayRecipeEditor: React.FC<DelayRecipeEditorProps> = props => {
    const { workflow, onCommit } = props;
    const {
        visible,
        editable,
        pending,
        failed,
        delay,
        unit,
        handleDelayChange,
        handleUnitsChange,
        commitDelayChange,
    } = useDelayRecipeEditor(workflow, onCommit);

    if (!visible) {
        return null;
    }

    return (
        <div className="space-y-1">
            <p>Workflow delay</p>
            <div className="flex items-center space-x-2">
                <Input
                    disabled={!editable}
                    textSize="sm"
                    className="flex-1"
                    value={delay}
                    type="number"
                    onChange={handleDelayChange}
                    onBlur={commitDelayChange}
                />
                <Select
                    disabled={!editable}
                    textSize="sm"
                    value={unit}
                    onChange={handleUnitsChange}>
                    <option value="m">Minutes</option>
                    <option value="h">Hours</option>
                    <option value="d">Days</option>
                </Select>
                {pending ? <Spinner /> : <div className="w-4 h-4" />}
            </div>
            {failed ? (
                <InlineBanner type="error">{WAIT_SAVE_FAILURE_MESSAGE}</InlineBanner>
            ) : null}
            <p className="text-sm text-gray-500">
                Set the delay to run this workflow minutes, hours, or days after the
                criteria is met. We recommend setting this to at least 1 minute for all
                workflows.
            </p>
        </div>
    );
};
